import { getReferenceString } from "@medplum/core";
import { BundleEntry, Condition, Procedure } from "@medplum/fhirtypes";

import { v4 as uuidv4 } from "uuid";

import { PROCEDURES_ENTERED_IN_ERROR } from "../constants/procedures";
import { ProceduresBundleContext, StagedProceduresOperation } from "../types/procedures";
import {
  getProceduresPickerInitialValues,
  ProceduresPickerFormType,
  proceduresPickerInitialValues,
} from "../zod-schemas/procedures";

import { buildEncounterProceduresBundle, EncounterProceduresData } from "./procedures";

type ApplyStagedProceduresParams = ProceduresBundleContext & {
  /** The encounter as the last read returned it: staged work is replayed on top of this. */
  serverData: EncounterProceduresData;
  operations: StagedProceduresOperation[];
};

/**
 * Marks a resource the tab is only previewing. A staged resource has no server id yet, so one is
 * invented to group the cards and key the lists; it never reaches a bundle that is sent.
 */
const STAGED_ID_PREFIX = "staged-";

const buildStagedId = () => `${STAGED_ID_PREFIX}${uuidv4()}`;

export const isStagedResourceId = (id: string | undefined): boolean =>
  Boolean(id?.startsWith(STAGED_ID_PREFIX));

// Cloned rather than referenced: a staged operation outlives the picker that made it, and it is what
// the encounter save replays, so it cannot share a live form object with anything still editing.
export const buildStagedProceduresOperation = (
  formValues: ProceduresPickerFormType,
  initialValues: ProceduresPickerFormType,
): StagedProceduresOperation => ({
  id: uuidv4(),
  formValues: structuredClone(formValues),
  initialValues: structuredClone(initialValues),
});

/**
 * Stages a diagnosis removal: the diagnosis and every procedure under it are loaded, then dropped.
 * Scoping the operation to those resources leaves the rest of the encounter alone, and retires
 * rather than deletes what is left pointing at nothing.
 */
export const buildDiagnosisRemovalOperation = (
  condition: Condition,
  procedures: Procedure[],
): StagedProceduresOperation =>
  buildStagedProceduresOperation(
    proceduresPickerInitialValues,
    getProceduresPickerInitialValues(procedures, [condition]),
  );

// Both reads filter these out, so the preview drops them too: the next operation has to reconcile
// against exactly what the next server read would hand back.
const isRetiredProcedure = (procedure: Procedure): boolean =>
  procedure.status === PROCEDURES_ENTERED_IN_ERROR;

const isRetiredCondition = (condition: Condition): boolean =>
  Boolean(
    condition.verificationStatus?.coding?.some(({ code }) => code === PROCEDURES_ENTERED_IN_ERROR),
  );

const upsertById = <T extends { id?: string }>(resources: T[], resource: T): void => {
  const index = resources.findIndex((item) => item.id && item.id === resource.id);

  if (index === -1) resources.push(resource);
  else resources[index] = resource;
};

// Folds one operation's bundle back into the in-memory encounter, so the next operation reconciles
// against the result of this one — the same way each write re-reads the encounter the one before it
// left behind.
//
// Conditions are applied first: a procedure written alongside a brand-new diagnosis points at the
// urn:uuid the transaction would have resolved server-side, and the tab groups its cards by
// reference, so those placeholders have to be resolved before the procedures are folded in.
const applyBundleEntries = (
  data: EncounterProceduresData,
  entries: BundleEntry[],
): EncounterProceduresData => {
  const conditions = [...data.conditions];
  const procedures = [...data.procedures];
  const resolvedConditionReferences = new Map<string, string>();

  for (const { resource, fullUrl } of entries) {
    if (resource?.resourceType !== "Condition") continue;

    const condition: Condition = resource.id ? resource : { ...resource, id: buildStagedId() };

    if (fullUrl) resolvedConditionReferences.set(fullUrl, getReferenceString(condition)!);

    upsertById(conditions, condition);
  }

  for (const { resource } of entries) {
    if (resource?.resourceType !== "Procedure") continue;

    const procedure: Procedure = {
      ...resource,
      id: resource.id ?? buildStagedId(),
      ...(resource.reasonReference
        ? {
            reasonReference: resource.reasonReference.map((reason) => ({
              ...reason,
              reference: reason.reference
                ? (resolvedConditionReferences.get(reason.reference) ?? reason.reference)
                : reason.reference,
            })),
          }
        : {}),
    };

    upsertById(procedures, procedure);
  }

  return {
    conditions: conditions.filter((condition) => !isRetiredCondition(condition)),
    procedures: procedures.filter((procedure) => !isRetiredProcedure(procedure)),
  };
};

/**
 * Replays the staged operations over the last server read, in the order the provider made them, and
 * returns the encounter as the save is going to leave it.
 *
 * The preview runs the same reconciliation the write does, so what the tab shows and what the
 * encounter save writes cannot drift apart.
 */
export const applyStagedProceduresOperations = ({
  serverData,
  operations,
  ...context
}: ApplyStagedProceduresParams): EncounterProceduresData =>
  operations.reduce((data, { formValues, initialValues }) => {
    const entries = buildEncounterProceduresBundle({
      formValues,
      initialValues,
      existingProcedures: data.procedures,
      existingConditions: data.conditions,
      ...context,
    });

    return entries.length ? applyBundleEntries(data, entries) : data;
  }, serverData);
